import { useForm, Controller } from 'react-hook-form';
import { object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { useHistory } from 'react-router-dom';
import { Form, ValidateField, RegisterInputField, ErrorSpace, ErrorText, RegisterButton, BackToLoginButton } from './RegisterForm.style';
import userIcon from '../../../assets/images/icons/user-alt.svg';
import emailIcon from '../../../assets/images/icons/envelope-solid-full.svg';
import { registerUser } from '../../../services/userRegistration.service';

const RegisterForm = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const validationSchema = object().shape({
    firstName: string().required(t('register.firstNameIsRequired')),
    lastName: string().required(t('register.lastNameIsRequired')),
    email: string()
      .email(t('register.emailInvalid'))
      .required(t('register.emailIsRequired'))
  });

  const {
    formState: { errors },
    control,
    handleSubmit
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur'
  });

  const onSubmit = async ({ firstName, lastName, email, password }) => {
    try {      
      const response = await registerUser({
        firstName,
        lastName,
        email
      });
      
      message.success(t('register.successMessage'));
      
      // Redirect to verification page with the identificator
      history.push('/verification', { 
        identificator: response.data.identificator 
      });
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different types of errors
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.response?.status === 400) {
        message.error(t('register.validationError'));
      } else if (error.response?.status === 409) {
        message.error(t('register.emailAlreadyExists'));
      } else {
        message.error(t('register.failedRegistration'));
      }
    }
  };

  const handleBackToLogin = () => {
    history.push('/');
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        key="firstName"
        name="firstName"
        defaultValue=""
        control={control}
        render={({ field }) => (
          <ValidateField>
            <RegisterInputField
              prefix={<ReactSVG src={userIcon} />}
              onChange={field.onChange}
              value={field.value}
              placeholder={t('register.firstName').toUpperCase()}
            />
            <ErrorSpace>{errors.firstName && <ErrorText>{errors.firstName.message}</ErrorText>}</ErrorSpace>
          </ValidateField>
        )}
      />
      <Controller
        key="lastName"
        name="lastName"
        defaultValue=""
        control={control}
        render={({ field }) => (
          <ValidateField>
            <RegisterInputField
              prefix={<ReactSVG src={userIcon} />}
              onChange={field.onChange}
              value={field.value}
              placeholder={t('register.lastName').toUpperCase()}
            />
            <ErrorSpace>{errors.lastName && <ErrorText>{errors.lastName.message}</ErrorText>}</ErrorSpace>
          </ValidateField>
        )}
      />
      <Controller
        key="email"
        name="email"
        defaultValue=""
        control={control}
        render={({ field }) => (
          <ValidateField>
            <RegisterInputField
              prefix={<ReactSVG src={emailIcon} />}
              onChange={field.onChange}
              value={field.value}
              placeholder={t('register.email').toUpperCase()}
            />
            <ErrorSpace>{errors.email && <ErrorText>{errors.email.message}</ErrorText>}</ErrorSpace>
          </ValidateField>
        )}
      />
      <RegisterButton htmltype="submit">{t('register.signUp')}</RegisterButton>
      <BackToLoginButton type="button" onClick={handleBackToLogin}>{t('register.backToLogin')}</BackToLoginButton>
    </Form>
  );
};

export default RegisterForm; 